/**
 * The catalog's live feed — `@authe/streaming` wired into `@authe/catalog`'s
 * `CatalogSubscribe` port.
 *
 * `catalog-mgmt` has published create/update/delete on the `entities` /
 * `groups` / `tags` / `labels` streams since the catalog tables got their
 * DynamoDB streams, and nothing on the front end listened. A list was only ever
 * as current as the moment it loaded: a teammate renaming a label, retiring a
 * tag, or the pipeline promoting an entity was invisible until somebody
 * reloaded, and nothing on screen said so.
 *
 * The mapping is small because both sides already exist. What this file owns:
 *
 *  - which stream a catalog kind is published on, and the fact that `address`
 *    (a `CatalogItemKind` in the UI) is NOT one of them — the addresses table
 *    has its own publisher and its own envelope, so it must not be silently
 *    subscribed here as if it were a catalog row;
 *  - the audience set: the tenant-wide channel AND the bare platform one,
 *    because a system-default catalog row is written at global tenancy and
 *    `channel_id` collapses it onto the plain stream name. Leaving the platform
 *    channel out would miss every change to the rows every tenant shares.
 */
import {
  subscribeDescriptor,
  streamChannelIdsForCaller,
  readResourceEvent,
  STREAM_ENTITIES,
  STREAM_GROUPS,
  STREAM_LABELS,
  STREAM_TAGS,
  type ChannelScope,
  type StreamMessage,
  type StreamingClient,
} from "@authe/streaming";
import type { CatalogChange, CatalogRow, CatalogSubscribe } from "@authe/catalog";
import type { CatalogItemKind } from "@authe/fe-commons/catalog";

/**
 * Catalog kind → the stream it is published on.
 *
 * `address` is deliberately absent. It is a `CatalogItemKind` on this surface,
 * but an address row is published by `address-mgmt-api` on `address-watch`, in
 * a different envelope, with the RAW DynamoDB event name. Mapping it here would
 * subscribe a feed whose events this reader cannot interpret.
 */
const STREAM_BY_KIND: Partial<Record<CatalogItemKind, string>> = {
  entity: STREAM_ENTITIES,
  group: STREAM_GROUPS,
  label: STREAM_LABELS,
  tag: STREAM_TAGS,
};

/** The stream a catalog kind is published on, or `undefined` when it has none. */
export function catalogStreamFor(kind: CatalogItemKind): string | undefined {
  return STREAM_BY_KIND[kind];
}

/**
 * Read a catalog stream message as a {@link CatalogChange}, or `null`.
 *
 * The row travels in `header` — the whole DynamoDB row minus the S3 body
 * pointer, with third-party provider identity redacted. A delete carries the
 * OLD image, which is still a usable row, so the id alone is what the deletion
 * needs and the header is passed through for the other two verbs.
 */
export function toCatalogChange(message: StreamMessage): CatalogChange | null {
  const event = readResourceEvent(message.payload);
  if (!event) return null;
  if (event.event === "deleted") return { event: "deleted", id: event.id };
  const header = event.header;
  // No projection means nothing to apply. Reported anyway: the id is enough for
  // the list to count it and offer a refresh, which beats dropping the news.
  const row = header ? ({ ...header, id: event.id } as CatalogRow) : undefined;
  return { event: event.event, id: event.id, row };
}

/**
 * A `CatalogSubscribe` over the shared streaming client, or `null` when the app
 * has no live transport configured (the lists then behave exactly as before).
 */
export function createCatalogSubscribe(
  client: StreamingClient | null | undefined,
  scope: ChannelScope,
  teamIds: ReadonlyArray<string> = [],
): CatalogSubscribe | null {
  if (!client) return null;
  return (kind, onChange) => {
    const stream = catalogStreamFor(kind);
    if (!stream) return () => {};
    // Every audience the caller belongs to, platform-wide channel included —
    // that one carries the system-default rows shared across tenants, and the
    // client de-duplicates an event that arrives on two of them.
    const offs = streamChannelIdsForCaller(scope, stream, teamIds).map((id) =>
      client.subscribeChannel(subscribeDescriptor(id), (message) => {
        const change = toCatalogChange(message);
        if (change) onChange(change);
      }),
    );
    return () => {
      for (const off of offs) off();
    };
  };
}
